import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  SimpleGrid,
  Switch,
  Text,
} from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Bank,
  Buildings,
  CaretRight,
  PencilSimple,
  Receipt,
  Scales,
  TrendDown,
  TrendUp,
} from 'phosphor-react';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { GlassCard, GlassPageBg } from '../../../../components/glass';
import {
  IDiretoriaDia,
  IDiretoriaFranquia,
  ISaldoConta,
  useFinanceiroComparativoFranquias,
  useFinanceiroDiretoria,
  useFinanceiroFluxo,
  useSetContaLimite,
} from '../../../../hooks/useFinanceiro';
import { isContaInterna, tipoLabel } from '../components/contaTipo';
import { FinanceiroPageHeader } from '../components/FinanceiroPageHeader';
import { brl } from '../components/format';
import { InfoPopover } from '../components/InfoPopover';
import { KpiCard } from '../components/KpiCard';
import { Segmented } from '../components/Segmented';
import { FIN, fmtPct } from '../components/theme';
import { useFinanceiroFilters } from '../components/useFinanceiroFilters';

const COR = '#1B88F5';

const fmtMes = (mes: string) => {
  const [y, m] = (mes || '').split('-');
  return y && m ? `${m}/${y.slice(2)}` : mes;
};

const compact = (v: number) => {
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1).replace('.', ',')}M`;
  if (abs >= 1e3) return `${Math.round(v / 1e3)}k`;
  return String(Math.round(v));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CaixaTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      bg="white"
      border="1px solid rgba(15,23,42,0.1)"
      borderRadius="md"
      boxShadow="lg"
      p={2.5}
      fontSize="xs"
    >
      <Text fontWeight={700} color={FIN.ink} mb={1}>
        {label}
      </Text>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {payload.map((p: any) => (
        <Flex key={p.dataKey} align="center" gap={2}>
          <Box w="8px" h="8px" borderRadius="sm" bg={p.color} />
          <Text color={FIN.muted}>{p.name}:</Text>
          <Text color={FIN.ink} fontWeight={600}>
            {brl(p.value)}
          </Text>
        </Flex>
      ))}
    </Box>
  );
};

/** Grade da tabela "Resultado por unidade": unidade + 4 números à direita. */
const COLS = 'minmax(0, 1.7fr) repeat(4, minmax(64px, 1fr))';

const CabecalhoUnidade = () => (
  <Box
    display="grid"
    gridTemplateColumns={COLS}
    gap={3}
    px={4}
    py={2}
    fontSize="11px"
    fontWeight={800}
    letterSpacing="0.04em"
    textTransform="uppercase"
    color={FIN.muted}
    borderBottom="1px solid"
    borderColor={FIN.hair}
  >
    <Text>Unidade</Text>
    <Text textAlign="right">Faturado</Text>
    <Text textAlign="right">Despesa</Text>
    <Text textAlign="right">Resultado</Text>
    <Text textAlign="right">Margem</Text>
  </Box>
);

interface LinhaUnidadeProps {
  nome: string;
  receita: number;
  despesa: number;
  resultado: number;
  margem: number;
  onClick?: () => void;
  total?: boolean;
}

const LinhaUnidade = ({
  nome,
  receita,
  despesa,
  resultado,
  margem,
  onClick,
  total,
}: LinhaUnidadeProps) => {
  const corResultado = resultado >= 0 ? FIN.ink : FIN.neg;
  return (
    <Box
      as={onClick ? 'button' : 'div'}
      w="full"
      textAlign="left"
      display="grid"
      gridTemplateColumns={COLS}
      gap={3}
      px={4}
      py={total ? 2.5 : 2}
      alignItems="center"
      bg={total ? FIN.blueSoft : 'transparent'}
      borderRadius={total ? 'lg' : 0}
      borderBottom={total ? undefined : '1px solid'}
      borderColor={FIN.hair}
      transition="background 0.12s"
      _hover={onClick ? { bg: 'rgba(27,136,245,0.06)' } : undefined}
      onClick={onClick}
    >
      <Flex align="center" gap={2} minW={0}>
        <Text
          fontSize="sm"
          fontWeight={total ? 800 : 600}
          color={FIN.ink}
          noOfLines={1}
          title={nome}
        >
          {nome}
        </Text>
        {onClick && <CaretRight size={12} color={FIN.faint} />}
      </Flex>
      <Text textAlign="right" fontSize="sm" fontWeight={total ? 800 : 500} color={FIN.ink}>
        {brl(receita)}
      </Text>
      <Text textAlign="right" fontSize="sm" fontWeight={total ? 800 : 500} color={FIN.muted}>
        {brl(despesa)}
      </Text>
      <Text
        textAlign="right"
        fontSize="sm"
        fontWeight={total ? 800 : 700}
        color={corResultado}
        className="tabular-nums"
      >
        {brl(resultado)}
      </Text>
      <Text textAlign="right" fontSize="sm" fontWeight={total ? 800 : 500} color={FIN.faint}>
        {receita > 0 ? fmtPct(margem) : '—'}
      </Text>
    </Box>
  );
};

const parseValor = (s: string) => {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

/** Um passo da esteira "Evolução do saldo" (inicial → +receb → −saídas → disponível). */
const SaldoStep = ({
  label,
  valor,
  cor,
  forte,
}: {
  label: string;
  valor: number;
  cor: string;
  forte?: boolean;
}) => (
  <Box
    px={{ base: 2.5, md: 3.5 }}
    py={2}
    borderRadius="xl"
    bg={forte ? `${cor}0F` : 'rgba(15,23,42,0.02)'}
    border="1px solid"
    borderColor={forte ? `${cor}33` : FIN.hair}
    flex={{ base: '1 1 40%', md: '0 1 auto' }}
  >
    <Text
      fontSize="10px"
      fontWeight={700}
      color={FIN.faint}
      textTransform="uppercase"
      letterSpacing="0.06em"
      noOfLines={1}
    >
      {label}
    </Text>
    <Text
      fontSize={{ base: 'sm', md: 'md' }}
      fontWeight={forte ? 800 : 700}
      color={cor}
      className="tabular-nums"
    >
      {brl(valor)}
    </Text>
  </Box>
);

const SaldoOp = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="lg" fontWeight={700} color={FIN.faint} flexShrink={0}>
    {children}
  </Text>
);

const SALDO_COLS = 'minmax(0, 2.2fr) minmax(84px, 1fr) minmax(104px, 1fr) minmax(112px, 1fr)';

// Nome amigável do banco (vem cru: SANTANDER, CONTAAZUL_IP, OUTROS…).
const bancoNome = (banco: string | null) => {
  const b = (banco || '').trim().toUpperCase();
  if (!b || b === 'OUTROS') return '';
  if (b === 'CONTAAZUL_IP') return 'Conta Azul IP';
  return b.charAt(0) + b.slice(1).toLowerCase();
};

// Sub-linha da conta: banco · agência · número (cai pro tipo se não há dados bancários).
const contaSubinfo = (c: ISaldoConta) => {
  const partes: string[] = [];
  const b = bancoNome(c.banco);
  if (b) partes.push(b);
  if (c.agencia) partes.push(`ag ${c.agencia}`);
  if (c.numero) partes.push(`cc ${c.numero}`);
  return partes.length ? partes.join(' · ') : tipoLabel(c.tipo);
};

/**
 * Linha da tabela "Saldo por conta" (grade): conta (nome + banco/agência) | saldo |
 * limite (editável no lápis; manual, o Conta Azul não expõe) | saldo + limite.
 */
const ContaSaldoRow = ({ conta }: { conta: ISaldoConta }) => {
  const { mutate, isPending } = useSetContaLimite();
  const [aberto, setAberto] = useState(false);
  const [valor, setValor] = useState('');

  const abrir = () => {
    setValor(String(conta.limite || 0));
    setAberto(true);
  };
  const salvar = () => {
    if (!conta.id) return;
    mutate({ contaId: conta.id, limite: parseValor(valor) }, { onSuccess: () => setAberto(false) });
  };

  return (
    <Box
      display="grid"
      gridTemplateColumns={SALDO_COLS}
      gap={3}
      alignItems="center"
      px={3}
      py={2}
      borderBottom="1px solid"
      borderColor={FIN.hair}
      _hover={{ bg: 'rgba(15,23,42,0.015)' }}
    >
      <Box minW={0}>
        <Text fontSize="sm" fontWeight={600} color={FIN.ink} noOfLines={1} title={conta.nome || ''}>
          {conta.nome || '—'}
        </Text>
        <Text fontSize="11px" color={FIN.faint} noOfLines={1}>
          {contaSubinfo(conta)}
        </Text>
      </Box>

      <Text
        textAlign="right"
        fontSize="sm"
        fontWeight={700}
        color={conta.saldo < 0 ? FIN.neg : FIN.ink}
        whiteSpace="nowrap"
        className="tabular-nums"
      >
        {brl(conta.saldo)}
      </Text>

      <Flex justify="flex-end">
        <Popover isOpen={aberto} onClose={() => setAberto(false)} placement="bottom-end" isLazy>
          <PopoverTrigger>
            <Flex
              as="button"
              type="button"
              align="center"
              gap={1}
              color={conta.limite > 0 ? FIN.muted : FIN.faint}
              _hover={{ color: FIN.info }}
              onClick={abrir}
              disabled={!conta.id}
            >
              <Text fontSize="sm" className="tabular-nums" whiteSpace="nowrap">
                {brl(conta.limite)}
              </Text>
              <PencilSimple size={11} />
            </Flex>
          </PopoverTrigger>
          <Portal>
            <PopoverContent w="220px" boxShadow="xl">
              <PopoverArrow />
              <PopoverBody>
                <Text fontSize="11px" fontWeight={700} color={FIN.muted} mb={1.5}>
                  Limite (cheque especial)
                </Text>
                <Input
                  size="sm"
                  type="number"
                  value={valor}
                  onChange={e => setValor(e.target.value)}
                  placeholder="0,00"
                  borderRadius="lg"
                  autoFocus
                />
                <Flex gap={2} mt={2} justify="flex-end">
                  <Button size="xs" variant="ghost" onClick={() => setAberto(false)}>
                    Cancelar
                  </Button>
                  <Button
                    size="xs"
                    bg={FIN.info}
                    color="white"
                    _hover={{ bg: '#1670cc' }}
                    onClick={salvar}
                    isLoading={isPending}
                  >
                    Salvar
                  </Button>
                </Flex>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      </Flex>

      <Text
        textAlign="right"
        fontSize="sm"
        fontWeight={700}
        color={conta.saldo_mais_limite < 0 ? FIN.neg : FIN.ink}
        whiteSpace="nowrap"
        className="tabular-nums"
      >
        {brl(conta.saldo_mais_limite)}
      </Text>
    </Box>
  );
};

// Cabeçalho de colunas da tabela de saldo.
const SaldoCabecalho = () => (
  <Box
    display="grid"
    gridTemplateColumns={SALDO_COLS}
    gap={3}
    px={3}
    py={2}
    fontSize="10px"
    fontWeight={800}
    letterSpacing="0.04em"
    textTransform="uppercase"
    color={FIN.faint}
    borderBottom="1px solid"
    borderColor={FIN.hair}
  >
    <Text>Conta</Text>
    <Text textAlign="right">Saldo</Text>
    <Text textAlign="right">Limite</Text>
    <Text textAlign="right">Saldo + limite</Text>
  </Box>
);

// Grupo de contas de uma franquia: banda com subtotal (saldo e saldo+limite) + as linhas.
const GrupoSaldo = ({ franquia, contas }: { franquia: string; contas: ISaldoConta[] }) => {
  const saldo = contas.reduce((s, c) => s + c.saldo, 0);
  const saldoMaisLimite = contas.reduce((s, c) => s + c.saldo_mais_limite, 0);
  return (
    <Box>
      <Box
        display="grid"
        gridTemplateColumns={SALDO_COLS}
        gap={3}
        px={3}
        py={1.5}
        alignItems="center"
        bg="rgba(27,136,245,0.05)"
        borderBottom="1px solid"
        borderColor={FIN.hair}
      >
        <Flex align="center" gap={1.5} minW={0}>
          <Text
            fontSize="11px"
            fontWeight={800}
            color={FIN.info}
            textTransform="uppercase"
            letterSpacing="0.04em"
            noOfLines={1}
          >
            {franquia}
          </Text>
          <Text fontSize="10px" color={FIN.faint} whiteSpace="nowrap">
            · {contas.length}
          </Text>
        </Flex>
        <Text
          textAlign="right"
          fontSize="xs"
          fontWeight={700}
          color={saldo < 0 ? FIN.neg : FIN.muted}
        >
          {brl(saldo)}
        </Text>
        <Box />
        <Text
          textAlign="right"
          fontSize="xs"
          fontWeight={700}
          color={saldoMaisLimite < 0 ? FIN.neg : FIN.muted}
        >
          {brl(saldoMaisLimite)}
        </Text>
      </Box>
      {contas.map(c => (
        <ContaSaldoRow key={`conta-${c.id ?? c.nome}`} conta={c} />
      ))}
    </Box>
  );
};

// Agrupa as contas por franquia (conta_origem); ordena grupos e contas por saldo desc.
const agruparPorFranquia = (contas: ISaldoConta[]) => {
  const grupos = new Map<string, ISaldoConta[]>();
  contas.forEach(c => {
    const chave = c.conta_origem || '—';
    const lista = grupos.get(chave);
    if (lista) lista.push(c);
    else grupos.set(chave, [c]);
  });
  return Array.from(grupos.entries())
    .map(([franquia, cs]) => ({
      franquia,
      contas: [...cs].sort((a, b) => b.saldo - a.saldo),
      saldo: cs.reduce((s, c) => s + c.saldo, 0),
    }))
    .sort((a, b) => b.saldo - a.saldo);
};

// ── Bloco "Posição de caixa por unidade" (waterfall do Excel) ────────────────
interface PosicaoUnidade {
  franquia: string;
  saldoAnterior: number;
  entrada: number;
  saida: number;
  retirada: number;
  disponivel: number;
  limite: number;
  maisLimite: number;
}

const CAIXA_COLS = 'minmax(120px,1.5fr) repeat(6, minmax(76px,1fr))';

const NumCell = ({ v, bold }: { v: number; bold?: boolean }) => (
  <Text
    textAlign="right"
    fontSize="xs"
    fontWeight={bold ? 800 : 600}
    color={v < 0 ? FIN.neg : FIN.ink}
  >
    {brl(v)}
  </Text>
);

const PosicaoCaixaUnidade = ({ linhas }: { linhas: PosicaoUnidade[] }) => {
  const tot = linhas.reduce(
    (a, l) => ({
      saldoAnterior: a.saldoAnterior + l.saldoAnterior,
      entrada: a.entrada + l.entrada,
      saida: a.saida + l.saida,
      disponivel: a.disponivel + l.disponivel,
      limite: a.limite + l.limite,
      maisLimite: a.maisLimite + l.maisLimite,
    }),
    { saldoAnterior: 0, entrada: 0, saida: 0, disponivel: 0, limite: 0, maisLimite: 0 },
  );
  return (
    <Box overflowX="auto">
      <Box minW="720px">
        <Box
          display="grid"
          gridTemplateColumns={CAIXA_COLS}
          gap={2}
          px={3}
          py={2}
          fontSize="10px"
          fontWeight={800}
          letterSpacing="0.04em"
          textTransform="uppercase"
          color={FIN.faint}
          borderBottom="1px solid"
          borderColor={FIN.hair}
        >
          <Text>Unidade</Text>
          <Text textAlign="right">Saldo ant.</Text>
          <Text textAlign="right">Entrada</Text>
          <Text textAlign="right">Saídas</Text>
          <Text textAlign="right">Disponível</Text>
          <Text textAlign="right">Limite</Text>
          <Text textAlign="right">Saldo + lim.</Text>
        </Box>
        {linhas.map(l => (
          <Box
            key={`pos-${l.franquia}`}
            display="grid"
            gridTemplateColumns={CAIXA_COLS}
            gap={2}
            px={3}
            py={2}
            alignItems="center"
            borderBottom="1px solid"
            borderColor={FIN.hair}
          >
            <Box minW={0}>
              <Text fontSize="xs" fontWeight={700} color={FIN.ink} noOfLines={1}>
                {l.franquia}
              </Text>
              {l.retirada > 0 && (
                <Text fontSize="10px" color={FIN.faint} noOfLines={1}>
                  retirada sócio {brl(l.retirada)}
                </Text>
              )}
            </Box>
            <NumCell v={l.saldoAnterior} />
            <NumCell v={l.entrada} />
            <NumCell v={l.saida} />
            <NumCell v={l.disponivel} bold />
            <NumCell v={l.limite} />
            <NumCell v={l.maisLimite} bold />
          </Box>
        ))}
        <Box
          display="grid"
          gridTemplateColumns={CAIXA_COLS}
          gap={2}
          px={3}
          py={2.5}
          alignItems="center"
          bg="rgba(27,136,245,0.08)"
          borderRadius="0 0 10px 10px"
        >
          <Text fontSize="11px" fontWeight={800} color={FIN.info} textTransform="uppercase">
            Total geral
          </Text>
          <NumCell v={tot.saldoAnterior} bold />
          <NumCell v={tot.entrada} bold />
          <NumCell v={tot.saida} bold />
          <NumCell v={tot.disponivel} bold />
          <NumCell v={tot.limite} bold />
          <NumCell v={tot.maisLimite} bold />
        </Box>
      </Box>
    </Box>
  );
};

// ── Bloco "Visão diária" (recebido x pago por dia, regime de caixa) ──────────
const VisaoDiaria = ({ dias }: { dias: IDiretoriaDia[] }) => {
  const data = dias.map(d => ({
    dia: `${d.dia.slice(8, 10)}/${d.dia.slice(5, 7)}`,
    Recebido: d.entradas,
    Pago: d.saidas,
  }));
  return (
    <Box h="220px">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={FIN.hair} vertical={false} />
          <XAxis
            dataKey="dia"
            tick={{ fontSize: 10, fill: FIN.faint }}
            interval="preserveStartEnd"
            minTickGap={16}
          />
          <YAxis tick={{ fontSize: 10, fill: FIN.faint }} tickFormatter={compact} width={44} />
          <Tooltip content={<CaixaTooltip />} />
          <Bar dataKey="Recebido" fill={COR} radius={[2, 2, 0, 0]} />
          <Bar dataKey="Pago" fill={FIN.neg} radius={[2, 2, 0, 0]} />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

export const FinanceiroDiretoriaPage = () => {
  const f = useFinanceiroFilters({ defaultPeriod: 'year' });
  const navigate = useNavigate();
  const { search } = useLocation();
  const [hideInternal, setHideInternal] = useState(true);
  const [graf, setGraf] = useState<'caixa' | 'diaria'>('caixa');
  const [tab, setTab] = useState<'resultado' | 'posicao' | 'saldo'>('resultado');

  const { data: fluxo, isError } = useFinanceiroFluxo({ base: 'competencia', ...f.apiParams });
  const { data: comp } = useFinanceiroComparativoFranquias({
    base: 'competencia',
    ...f.apiParams,
  });

  const meses = fluxo?.meses ?? [];
  const totalEntradas = meses.reduce((s, m) => s + m.entradas, 0);
  const totalSaidas = meses.reduce((s, m) => s + m.saidas, 0);
  const resultado = totalEntradas - totalSaidas;

  // Contas internas (Outros/Aplicação/Cartão) são movimentos não-operacionais.
  // Ocultá-las (padrão) remove-as da lista E do saldo que alimenta os KPIs.
  const todasContas = fluxo?.saldo_contas ?? [];
  const contasInternas = todasContas.filter(c => isContaInterna(c.tipo));
  const contas = hideInternal ? todasContas.filter(c => !isContaInterna(c.tipo)) : todasContas;
  const saldoContas = contas.reduce((s, c) => s + c.saldo, 0);
  const totalMaisLimite = contas.reduce((s, c) => s + (c.saldo_mais_limite || 0), 0);
  const limiteTotal = contas.reduce((s, c) => s + (c.limite || 0), 0);
  const gruposSaldo = useMemo(() => agruparPorFranquia(contas), [contas]);

  // Regime de caixa (data real de baixa) por franquia + por dia — Bloco 2 do Excel.
  // O endpoint /diretoria ignora `base` (sempre usa data de baixa); mandamos
  // 'caixa' só pra o contrato ficar honesto.
  const { data: diretoria } = useFinanceiroDiretoria({ base: 'caixa', ...f.apiParams });
  // Waterfall respeita o "ocultar internas" (mesmo escopo de conta dos KPIs e do
  // Saldo por conta) e une franquias com baixa mas sem conta cadastrada. As
  // entradas/saídas vêm das baixas (por franquia, sem distinção de conta).
  const posicaoUnidades: PosicaoUnidade[] = useMemo(() => {
    const saldoMap = new Map<string, { saldo: number; limite: number }>();
    const chaves = new Set<string>();
    gruposSaldo.forEach(g => {
      saldoMap.set(g.franquia, {
        saldo: g.saldo,
        limite: g.contas.reduce((s, c) => s + (c.limite || 0), 0),
      });
      chaves.add(g.franquia);
    });
    const baixaMap = new Map<string, IDiretoriaFranquia>();
    (diretoria?.por_franquia ?? []).forEach(x => {
      const k = x.conta_origem || '—';
      baixaMap.set(k, x);
      chaves.add(k);
    });
    return Array.from(chaves)
      .map(franquia => {
        const sl = saldoMap.get(franquia);
        const d = baixaMap.get(franquia);
        const entrada = d?.entradas ?? 0;
        const saida = d?.saidas ?? 0;
        const disponivel = sl?.saldo ?? 0;
        const limite = sl?.limite ?? 0;
        return {
          franquia,
          saldoAnterior: disponivel - (entrada - saida),
          entrada,
          saida,
          retirada: d?.retirada_socios ?? 0,
          disponivel,
          limite,
          maisLimite: disponivel + limite,
        };
      })
      .sort((a, b) => b.disponivel - a.disponivel);
  }, [diretoria, gruposSaldo]);
  const diasCaixa = diretoria?.por_dia ?? [];

  // Saldo inicial do período reconstruído do saldo atual: saldo_inicial =
  // saldo_atual − resultado. Exato quando o período termina hoje (uso executivo:
  // mês/ano corrente, YTD — no futuro não há "pago"); histórico puro é aproximado.
  const saldoInicial = saldoContas - resultado;

  // Evolução do saldo: a linha parte do saldo inicial e termina no saldo atual
  // (saldo_inicial + resultado acumulado). É a curva "saldo inicial → … → saldo
  // final" pedida pelo MODELO.
  const chart = useMemo(() => {
    let acc = saldoInicial;
    return meses.map(m => {
      acc += m.resultado;
      return { mes: fmtMes(m.mes), entradas: m.entradas, saidas: m.saidas, saldo: acc };
    });
  }, [meses, saldoInicial]);

  const unidades = useMemo(
    () => [...(comp?.itens ?? [])].sort((a, b) => b.receita - a.receita),
    [comp],
  );

  const irPara = (path: string) => navigate({ pathname: path, search });
  const irParaUnidade = (franquia: string) => {
    const sp = new URLSearchParams(search);
    sp.set('conta_origem', franquia);
    sp.set('page', '1');
    navigate({ pathname: '/modulesHome/financeiro/fluxo', search: sp.toString() });
  };

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <FinanceiroPageHeader
          eyebrow="Diretoria"
          titulo="Dashboard Diretoria"
          subtitulo="Recebimentos, saídas e evolução do caixa — visão executiva por unidade"
          icon={<Scales size={24} weight="fill" />}
          cor={COR}
          info={
            <Text>
              Visão executiva do caixa: <b>recebido × pago</b> (regime de caixa) no período, a{' '}
              <b>evolução do caixa</b> mês a mês e o <b>resultado por unidade</b>. Clique num número
              ou numa unidade para ver o detalhe.
            </Text>
          }
        />

        {isError && (
          <GlassCard intensity="medium" p={6} mb={5} tint={FIN.neg}>
            <Text color="#71717A">Não foi possível carregar os dados do caixa.</Text>
          </GlassCard>
        )}

        {/* Tira executiva — os números do rodapé do relatório diário */}
        <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={4} mb={6}>
          <KpiCard
            label="Recebimentos"
            valor={totalEntradas}
            color={FIN.pos}
            icon={<TrendUp size={20} weight="duotone" />}
            index={0}
            onClick={() => irPara('/modulesHome/financeiro/contas-receber')}
            info={
              <InfoPopover title="Recebimentos">
                Total <b>recebido</b> (RECEITA paga) no período — regime de caixa. Clique para ver
                os títulos.
              </InfoPopover>
            }
          />
          <KpiCard
            label="Saídas"
            valor={totalSaidas}
            color={FIN.neg}
            icon={<TrendDown size={20} weight="duotone" />}
            index={1}
            onClick={() => irPara('/modulesHome/financeiro/contas-pagar')}
            info={
              <InfoPopover title="Saídas">
                Total <b>pago</b> (DESPESA paga) no período — regime de caixa. Clique para ver as
                despesas.
              </InfoPopover>
            }
          />
          <KpiCard
            label="Resultado do período"
            valor={resultado}
            color={resultado >= 0 ? FIN.pos : FIN.neg}
            icon={<Scales size={20} weight="duotone" />}
            index={2}
            onClick={() => irPara('/modulesHome/financeiro/fluxo')}
            info={
              <InfoPopover title="Resultado de caixa">
                Recebimentos − saídas do período. Positivo = o caixa cresceu; negativo = consumiu
                saldo.
              </InfoPopover>
            }
          />
          <KpiCard
            label="Saldo em contas"
            valor={saldoContas}
            color={FIN.info}
            icon={<Bank size={20} weight="duotone" />}
            index={3}
            onClick={() => irPara('/modulesHome/financeiro/fluxo')}
            info={
              <InfoPopover title="Saldo em contas">
                Soma do saldo <b>atual</b> das contas do Conta Azul — retrato de <b>hoje</b>, não
                depende do período. Exclui contas internas (Outros/Aplicação/Cartão) quando o filtro
                está ligado.
              </InfoPopover>
            }
          />
        </SimpleGrid>

        {/* Toggle global: ocultar contas internas — afeta os blocos de saldo */}
        {contasInternas.length > 0 && (
          <Flex justify="flex-end" align="center" gap={1.5} mb={4}>
            <Switch
              size="sm"
              colorScheme="blue"
              isChecked={hideInternal}
              onChange={e => setHideInternal(e.target.checked)}
            />
            <Text fontSize="11px" color={FIN.faint} whiteSpace="nowrap">
              Ocultar contas internas ({contasInternas.length})
            </Text>
          </Flex>
        )}

        {/* Evolução do saldo — esteira: inicial → +recebimentos → −saídas → disponível */}
        <GlassCard intensity="medium" p={{ base: 3, md: 4 }} mb={5}>
          <Flex align="center" gap={1} mb={3}>
            <Text
              fontSize="11px"
              fontWeight={800}
              color={FIN.muted}
              textTransform="uppercase"
              letterSpacing="0.1em"
            >
              Evolução do saldo · {f.monthLabel}
            </Text>
            <InfoPopover title="Evolução do saldo">
              <Text>
                <b>Saldo inicial</b> = saldo atual − resultado do período. Some os{' '}
                <b>recebimentos</b>, subtrai as <b>saídas</b> e chega no <b>saldo disponível</b> de
                hoje. Exato quando o período vai até hoje (mês/ano corrente); período 100% passado é
                aproximado.
              </Text>
            </InfoPopover>
          </Flex>
          <Flex align="center" gap={{ base: 2, md: 3 }} wrap="wrap">
            <SaldoStep label="Saldo inicial" valor={saldoInicial} cor={FIN.ink} />
            <SaldoOp>+</SaldoOp>
            <SaldoStep label="Recebimentos" valor={totalEntradas} cor={FIN.pos} />
            <SaldoOp>−</SaldoOp>
            <SaldoStep label="Saídas" valor={totalSaidas} cor={FIN.neg} />
            <SaldoOp>=</SaldoOp>
            <SaldoStep label="Saldo disponível" valor={saldoContas} cor={FIN.info} forte />
          </Flex>
        </GlassCard>

        {/* Módulo Gráficos — alterna entre os gráficos */}
        <Flex justify="center" mb={3}>
          <Segmented
            value={graf}
            onChange={v => setGraf(v as 'caixa' | 'diaria')}
            accent={FIN.info}
            options={[
              { value: 'caixa', label: 'Evolução do caixa' },
              { value: 'diaria', label: 'Visão diária' },
            ]}
          />
        </Flex>

        {graf === 'caixa' && (
          <>
            {/* Evolução do caixa — entradas × saídas + saldo */}
            <GlassCard intensity="strong" p={{ base: 3, md: 6 }} mb={5}>
              <Flex align="center" gap={1} mb={4} wrap="wrap">
                <Text fontWeight={700} color={FIN.ink}>
                  Evolução do caixa —{' '}
                  <Text as="span" color={FIN.pos}>
                    recebido
                  </Text>{' '}
                  ×{' '}
                  <Text as="span" color={FIN.neg}>
                    pago
                  </Text>{' '}
                  ·{' '}
                  <Text as="span" color={COR}>
                    saldo
                  </Text>
                </Text>
                <InfoPopover title="Evolução do caixa">
                  <Text>
                    Barras: <b>recebido</b> e <b>pago</b> por mês (regime de caixa). Linha: o{' '}
                    <b>saldo</b> mês a mês, partindo do <b>saldo inicial</b> e terminando no{' '}
                    <b>saldo atual</b>. É a curva de evolução do saldo pedida pela diretoria.
                  </Text>
                </InfoPopover>
              </Flex>
              {chart.length === 0 ? (
                <Text fontSize="sm" color={FIN.faint} py={8} textAlign="center">
                  Sem movimentação no período.
                </Text>
              ) : (
                <Box h="320px">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gCaixa" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={COR} stopOpacity={0.28} />
                          <stop offset="100%" stopColor={COR} stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(15,23,42,0.06)"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="mes"
                        tick={{ fontSize: 11, fill: FIN.faint }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tickFormatter={compact}
                        tick={{ fontSize: 11, fill: FIN.faint }}
                        axisLine={false}
                        tickLine={false}
                        width={48}
                      />
                      <Tooltip
                        content={<CaixaTooltip />}
                        cursor={{ fill: 'rgba(15,23,42,0.03)' }}
                      />
                      <ReferenceLine y={0} stroke="rgba(15,23,42,0.18)" />
                      <Bar
                        dataKey="entradas"
                        name="Recebido"
                        fill={FIN.pos}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={26}
                      />
                      <Bar
                        dataKey="saidas"
                        name="Pago"
                        fill={FIN.neg}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={26}
                      />
                      <Area
                        type="monotone"
                        dataKey="saldo"
                        name="Saldo"
                        stroke={COR}
                        strokeWidth={2.5}
                        fill="url(#gCaixa)"
                        dot={{ r: 2.5, fill: COR }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </GlassCard>
          </>
        )}

        {graf === 'diaria' && diasCaixa.length > 0 && (
          <GlassCard intensity="strong" p={{ base: 3, md: 5 }} mb={5}>
            <Flex align="center" gap={1} mb={3}>
              <Receipt size={18} weight="duotone" color={FIN.info} />
              <Text fontWeight={700} color={FIN.ink}>
                Visão diária — recebido × pago
              </Text>
              <InfoPopover title="Visão diária">
                Recebido (azul) e pago (vermelho) por <b>dia</b>, pela data real de baixa no
                período. Estreite o período (mês/semana) para leitura diária fiel.
              </InfoPopover>
            </Flex>
            <VisaoDiaria dias={diasCaixa} />
          </GlassCard>
        )}

        {/* Módulo Tabelas — 3 abas, uma por tabela */}
        <Flex justify="center" mb={3}>
          <Segmented
            value={tab}
            onChange={v => setTab(v as 'resultado' | 'posicao' | 'saldo')}
            accent={FIN.info}
            options={[
              { value: 'resultado', label: 'Resultado por unidade' },
              { value: 'posicao', label: 'Posição de caixa' },
              { value: 'saldo', label: 'Saldo por conta' },
            ]}
          />
        </Flex>

        {tab === 'resultado' && (
          <>
            {/* Resultado por unidade — o agrupamento que a diretoria lê (com total) */}
            <GlassCard intensity="strong" p={{ base: 3, md: 5 }} mb={5}>
              <Flex align="center" gap={1} mb={3} wrap="wrap">
                <Buildings size={18} weight="duotone" color={COR} />
                <Text fontWeight={700} color={FIN.ink}>
                  Resultado por unidade
                </Text>
                <InfoPopover title="Resultado por unidade">
                  <Text>
                    Receita e despesa <b>faturadas</b> (regime de competência) por franquia
                    (conta_origem), com margem. Ordenado pela receita. Clique numa unidade para
                    abrir o fluxo de caixa dela.
                  </Text>
                  <Text mt={2} color={FIN.muted}>
                    Base <b>faturado</b> ≠ os KPIs de caixa acima (recebido/pago): aqui conta o que
                    foi emitido, não o que entrou no banco.
                  </Text>
                </InfoPopover>
                <Text ml="auto" fontSize="xs" color={FIN.faint}>
                  faturado · competência
                </Text>
              </Flex>

              {unidades.length === 0 ? (
                <Text fontSize="sm" color={FIN.faint} py={8} textAlign="center">
                  Sem unidades no período.
                </Text>
              ) : (
                <Box>
                  <CabecalhoUnidade />
                  {unidades.map(u => (
                    <LinhaUnidade
                      key={u.franquia}
                      nome={u.franquia || '—'}
                      receita={u.receita}
                      despesa={u.despesa}
                      resultado={u.resultado}
                      margem={u.margem}
                      onClick={() => irParaUnidade(u.franquia)}
                    />
                  ))}
                  <Box mt={2}>
                    <LinhaUnidade
                      nome="TOTAL GERAL"
                      receita={comp?.total_receita ?? 0}
                      despesa={comp?.total_despesa ?? 0}
                      resultado={comp?.total_resultado ?? 0}
                      margem={
                        (comp?.total_receita ?? 0) > 0
                          ? ((comp?.total_resultado ?? 0) / (comp?.total_receita ?? 1)) * 100
                          : 0
                      }
                      total
                    />
                  </Box>
                </Box>
              )}
            </GlassCard>
          </>
        )}

        {tab === 'posicao' && (
          <>
            {/* Posição de caixa por unidade — waterfall executivo (Bloco 2 do Excel) */}
            <GlassCard intensity="strong" p={{ base: 3, md: 5 }} mb={5}>
              <Flex align="center" gap={1} mb={3}>
                <Scales size={18} weight="duotone" color={FIN.info} />
                <Text fontWeight={700} color={FIN.ink}>
                  Posição de caixa por unidade
                </Text>
                <InfoPopover title="Posição de caixa por unidade">
                  Regime de <b>caixa</b> (data real de baixa) no período: <b>saldo anterior</b> +{' '}
                  <b>entrada</b> − <b>saídas</b> = <b>disponível</b> (saldo de hoje), mais o{' '}
                  <b>limite</b> e o <b>saldo + limite</b> (folga de sobrevivência). Retirada de
                  sócio destacada sob cada unidade; vermelho = negativo. O <b>saldo anterior</b>{' '}
                  deriva do saldo de hoje menos o resultado do período — exato quando o período
                  termina hoje (mês/ano corrente); histórico puro é aproximado.
                </InfoPopover>
              </Flex>
              {posicaoUnidades.length === 0 ? (
                <Text fontSize="sm" color={FIN.faint} py={4}>
                  Sem movimento no período/filtro selecionado.
                </Text>
              ) : (
                <PosicaoCaixaUnidade linhas={posicaoUnidades} />
              )}
            </GlassCard>
          </>
        )}

        {tab === 'saldo' && (
          <>
            {/* Saldo por conta (hoje) — detalhe por conta bancária, agrupado por franquia */}
            <GlassCard intensity="strong" p={{ base: 3, md: 5 }} mb={5}>
              <Flex align="center" gap={1} mb={3}>
                <Bank size={18} weight="duotone" color={FIN.info} />
                <Text fontWeight={700} color={FIN.ink}>
                  Saldo por conta
                </Text>
                <InfoPopover title="Saldo por conta">
                  Saldo <b>atual</b> de cada conta do Conta Azul (retrato de hoje) + o <b>limite</b>{' '}
                  (cheque especial, digitado no lápis) e o <b>saldo + limite</b> — a folga real.
                  Vermelho = negativo. Por padrão <b>exclui</b> contas internas
                  (Outros/Aplicação/Cartão).
                </InfoPopover>
              </Flex>
              {contas.length === 0 && (
                <Text fontSize="sm" color={FIN.faint} py={4}>
                  {hideInternal && contasInternas.length > 0
                    ? 'Só há contas internas — desligue o filtro para exibir.'
                    : 'Sem contas financeiras.'}
                </Text>
              )}
              {contas.length > 0 && (
                <Box>
                  <SaldoCabecalho />
                  {gruposSaldo.map(g => (
                    <GrupoSaldo key={g.franquia} franquia={g.franquia} contas={g.contas} />
                  ))}
                  {/* TOTAL GERAL — soma das contas exibidas (destaque azul, como o Excel) */}
                  <Box
                    display="grid"
                    gridTemplateColumns={SALDO_COLS}
                    gap={3}
                    alignItems="center"
                    px={3}
                    py={2.5}
                    mt={1}
                    bg={FIN.blueSoft}
                    borderRadius="lg"
                  >
                    <Text fontWeight={800} color={FIN.ink}>
                      TOTAL GERAL
                    </Text>
                    <Text
                      textAlign="right"
                      fontWeight={800}
                      color={saldoContas < 0 ? FIN.neg : FIN.ink}
                      className="tabular-nums"
                    >
                      {brl(saldoContas)}
                    </Text>
                    <Text
                      textAlign="right"
                      fontWeight={800}
                      color={FIN.muted}
                      className="tabular-nums"
                    >
                      {brl(limiteTotal)}
                    </Text>
                    <Text
                      textAlign="right"
                      fontWeight={800}
                      color={totalMaisLimite < 0 ? FIN.neg : FIN.ink}
                      className="tabular-nums"
                    >
                      {brl(totalMaisLimite)}
                    </Text>
                  </Box>
                </Box>
              )}
            </GlassCard>
          </>
        )}
      </Box>
    </GlassPageBg>
  );
};
